<template>
  <div v-if="activo && paso" class="pos-tour" role="dialog" aria-modal="true" :aria-label="paso.titulo">
    <div class="pos-tour__capa" :class="{ 'pos-tour__capa--oscura': !caja }" @click.stop></div>
    <div v-if="caja" class="pos-tour__foco" :style="estiloFoco"></div>
    <div ref="tarjeta" class="pos-tour__tarjeta" :style="estiloTarjeta">
      <div class="pos-tour__cuenta">Paso {{ indice + 1 }} de {{ pasos.length }}</div>
      <h3 class="pos-tour__titulo">{{ paso.titulo }}</h3>
      <p class="pos-tour__cuerpo">{{ paso.cuerpo }}</p>
      <div class="pos-tour__acciones">
        <v-btn text small color="grey darken-1" @click="terminar(false)">{{ ultimo ? "Cerrar" : "Omitir" }}</v-btn>
        <v-spacer />
        <v-btn v-if="indice > 0" text small color="primary" @click="mover(-1)">Atrás</v-btn>
        <v-btn depressed small color="primary" @click="ultimo ? terminar(true) : mover(1)">{{ ultimo ? "Entendido" : "Siguiente" }}</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { pasosDe, tourDeRuta, toursDeVentana, tipoDeNegocio } from "@/plugins/pos/tours";

const MARGEN = 8;

/**
 * Recorrido guiado del POS. Al entrar a una pantalla con recorrido que el usuario aún no vio, lo ofrece;
 * al terminarlo u omitirlo el servidor lo guarda (`pos/tours/{clave}`) y no vuelve a salir. Si no hay
 * conexión no se muestra nada: sin saber qué vio, mejor no insistir.
 */
export default {
  name: "PosTour",
  data: () => ({ selectorVentana: null, sondeo: null, ventanaVista: null, vistos: null, clave: null, pasos: [], indice: 0, activo: false, caja: null, tamano: { w: 0, h: 0 }, temporizador: null }),
  computed: {
    tipo() {
      return tipoDeNegocio(this.$store.getters);
    },
    paso() {
      return this.pasos[this.indice];
    },
    ultimo() {
      return this.indice === this.pasos.length - 1;
    },
    estiloFoco() {
      const c = this.caja;
      return { top: c.top - MARGEN + "px", left: c.left - MARGEN + "px", width: c.width + MARGEN * 2 + "px", height: c.height + MARGEN * 2 + "px" };
    },
    estiloTarjeta() {
      const ancho = Math.min(340, window.innerWidth - 24);
      const alto = this.tamano.h || 190;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (!this.caja) {
        return { width: ancho + "px", top: Math.max(12, (vh - alto) / 2) + "px", left: (vw - ancho) / 2 + "px" };
      }
      const c = this.caja;
      const lado = this.paso.lado || "bottom";
      let top;
      let left;
      if (lado === "right") {
        left = c.left + c.width + MARGEN + 12;
        top = c.top;
      } else if (lado === "left") {
        left = c.left - ancho - MARGEN - 12;
        top = c.top;
      } else if (lado === "top") {
        top = c.top - alto - MARGEN - 12;
        left = c.left + c.width / 2 - ancho / 2;
      } else {
        top = c.top + c.height + MARGEN + 12;
        left = c.left + c.width / 2 - ancho / 2;
      }
      // Si no cabe del lado pedido (pantalla pequeña), va al centro de la pantalla sin tapar nada vital.
      if (left < 12 || left + ancho > vw - 12 || top < 12 || top + alto > vh - 12) {
        top = c.top + c.height / 2 > vh / 2 ? 12 : vh - alto - 12;
        left = (vw - ancho) / 2;
      }
      return { width: ancho + "px", top: top + "px", left: left + "px" };
    },
  },
  watch: {
    "$route.path"() {
      this.cerrarSinGuardar();
      this.programar();
    },
    "$store.getters.user"(u) {
      if (u) this.cargarVistos().then(this.programar);
    },
  },
  mounted() {
    window.addEventListener("resize", this.medir);
    window.addEventListener("keydown", this.tecla);
    this.$root.$on("pos-tour:iniciar", this.iniciarManual);
    // Las ventanas (cobro, mesas, fórmula…) no cambian la ruta: se las busca en pantalla cada segundo.
    this.sondeo = setInterval(this.buscarVentanas, 1000);
    if (this.$store.getters.user) this.cargarVistos().then(this.programar);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.medir);
    window.removeEventListener("keydown", this.tecla);
    this.$root.$off("pos-tour:iniciar", this.iniciarManual);
    clearTimeout(this.temporizador);
    clearInterval(this.sondeo);
  },
  methods: {
    async cargarVistos() {
      if (this.vistos) return;
      try {
        const r = await this.$http.get("api/v1/pos/tours", {}, false, false, false);
        this.vistos = (r && r.items) || [];
      } catch (e) {
        this.vistos = null;
      }
    },
    // Espera a que la pantalla termine de pintarse y a que la caja esté lista (turno abierto) antes de ofrecerlo.
    programar() {
      clearTimeout(this.temporizador);
      const clave = tourDeRuta(this.$route.path, this.tipo);
      if (!clave || !this.vistos || this.vistos.includes(clave)) return;
      this.temporizador = setTimeout(() => {
        if (this.activo || this.hayModalAbierto()) return this.programar();
        if (clave === "inicio" && !(this.$store.getters.installation && this.$store.getters.turn)) return this.programar();
        this.iniciar(clave);
      }, 1500);
    },
    hayModalAbierto() {
      return !!document.querySelector(".v-dialog--active");
    },
    // Una ventana de recorrido se ofrece cuando se ve en pantalla y el usuario no la ha visto todavía; a la segunda lectura,
    // para que haya terminado de abrirse.
    buscarVentanas() {
      // Si el usuario cierra la ventana a mitad del recorrido, el recorrido se va con ella.
      if (this.activo && this.selectorVentana && !this.hayVentana(this.selectorVentana)) return this.cerrarSinGuardar();
      if (this.activo || !this.vistos) return;
      const visible = toursDeVentana(this.$route.path, this.tipo).find(([clave, selector]) => this.hayVentana(selector) && !this.vistos.includes(clave));
      if (!visible) return (this.ventanaVista = null);
      if (this.ventanaVista !== visible[0]) return (this.ventanaVista = visible[0]);
      this.ventanaVista = null;
      this.iniciar(visible[0]);
      this.selectorVentana = visible[1];
    },
    hayVentana(selector) {
      const el = document.querySelector(selector);
      return !!el && el.getBoundingClientRect().width > 0;
    },
    iniciarManual() {
      const clave = tourDeRuta(this.$route.path, this.tipo);
      if (clave) this.iniciar(clave);
    },
    iniciar(clave) {
      this.clave = clave;
      // Solo los pasos que se pueden mostrar ahora: así «Paso x de y» cuenta lo que el usuario verá.
      this.pasos = pasosDe(clave, this.tipo).filter((p) => !p.selector || this.buscar(p));
      this.indice = 0;
      this.activo = true;
      this.irA(0);
    },
    buscar(paso) {
      if (!paso.selector) return null;
      const el = [...document.querySelectorAll(paso.selector)].find((e) => {
        const r = e.getBoundingClientRect();
        const enPantalla = r.right > 0 && r.bottom > 0 && r.left < window.innerWidth && r.top < window.innerHeight;
        return enPantalla && r.width > 0 && r.height > 0 && (!paso.texto || (e.textContent || "").includes(paso.texto));
      });
      return el || null;
    },
    mover(dir) {
      const i = this.indice + dir;
      if (i >= 0 && i < this.pasos.length) this.irA(i);
    },
    irA(i) {
      this.indice = i;
      this.$nextTick(() => {
        const el = this.buscar(this.paso);
        if (el) el.scrollIntoView({ block: "nearest", inline: "nearest" });
        this.medir();
      });
    },
    medir() {
      if (!this.activo || !this.paso) return;
      const el = this.buscar(this.paso);
      if (el) {
        const r = el.getBoundingClientRect();
        this.caja = { top: r.top, left: r.left, width: r.width, height: r.height };
      } else {
        this.caja = null;
      }
      this.$nextTick(() => {
        if (this.$refs.tarjeta) this.tamano = { w: this.$refs.tarjeta.offsetWidth, h: this.$refs.tarjeta.offsetHeight };
      });
    },
    tecla(e) {
      if (!this.activo) return;
      if (e.key === "Escape") this.terminar(false);
      else if (e.key === "ArrowRight") this.ultimo ? this.terminar(true) : this.mover(1);
      else if (e.key === "ArrowLeft" && this.indice > 0) this.mover(-1);
    },
    cerrarSinGuardar() {
      this.activo = false;
      this.selectorVentana = null;
      this.caja = null;
    },
    // Terminar y omitir se guardan igual: quien lo omite no quiere que se lo vuelvan a ofrecer.
    async terminar() {
      const clave = this.clave;
      this.cerrarSinGuardar();
      if (!clave || (this.vistos && this.vistos.includes(clave))) return;
      if (this.vistos) this.vistos.push(clave);
      try {
        await this.$http.post("api/v1/pos/tours/" + clave, {}, false, false, false);
      } catch (e) {
        /* Sin conexión: se vuelve a ofrecer la próxima vez; no vale la pena molestar al cajero. */
      }
    },
  },
};
</script>

<style scoped>
.pos-tour { position: fixed; inset: 0; z-index: 2000; }
.pos-tour__capa { position: absolute; inset: 0; }
.pos-tour__foco {
  position: fixed; border-radius: 10px; pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.62); outline: 2px solid #fff;
  transition: top .2s, left .2s, width .2s, height .2s;
}
.pos-tour__capa--oscura { background: rgba(15, 23, 42, 0.62); }
.pos-tour__tarjeta {
  position: fixed; background: #fff; border-radius: 12px; padding: 16px 18px 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35); transition: top .2s, left .2s;
}
.pos-tour__cuenta { font-size: 12px; color: #6b7280; margin-bottom: 4px; }
.pos-tour__titulo { font-size: 18px; line-height: 1.25; margin: 0 0 6px; }
.pos-tour__cuerpo { font-size: 14px; line-height: 1.45; color: #374151; margin: 0 0 12px; }
.pos-tour__acciones { display: flex; align-items: center; gap: 4px; }
</style>
